import { useId, type CSSProperties, type SVGProps } from "react";
import { MembershipCrestArtwork } from "./membershipCrestArtwork";
import type { CanonicalMembershipType } from "@dj/convex-api";

export type MembershipCrestSize =
  | "avatar-small"
  | "avatar-large"
  | "purchase"
  | "description";

export type MembershipCrestProps = Readonly<{
  ariaLabel?: string;
  className?: string;
  decorative?: boolean;
  membershipType: CanonicalMembershipType;
  size: MembershipCrestSize;
}>;

export const membershipDisplayNames: Readonly<
  Record<CanonicalMembershipType, string>
> = {
  community: "Community",
  pilot_operator: "Pilot Operator",
  pilot_professional: "Pilot Pro",
  drone_business: "Drone Business",
  enterprise: "Enterprise",
};

const crestPixels: Readonly<Record<MembershipCrestSize, number>> = {
  "avatar-small": 18,
  "avatar-large": 32,
  purchase: 28,
  description: 56,
};

export function isCanonicalMembershipType(
  value: string,
): value is CanonicalMembershipType {
  return value in membershipDisplayNames;
}

export function MembershipCrest({
  ariaLabel,
  className,
  decorative = false,
  membershipType,
  size,
}: MembershipCrestProps) {
  const gradientId = `membership-gradient-${useId()}`;
  const pixels = crestPixels[size];
  const label =
    ariaLabel ?? `${membershipDisplayNames[membershipType]} membership`;
  const style: CSSProperties = {
    display: "block",
    flex: "0 0 auto",
    height: pixels,
    width: pixels,
  };
  const accessibilityProps: SVGProps<SVGSVGElement> = decorative
    ? { "aria-hidden": true }
    : { "aria-label": label, role: "img" };

  return (
    <svg
      {...accessibilityProps}
      className={className}
      data-membership={membershipType}
      data-membership-crest="true"
      data-size={size}
      style={style}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
    >
      {decorative ? null : <title>{label}</title>}
      <MembershipCrestArtwork
        gradientId={gradientId}
        membershipType={membershipType}
      />
    </svg>
  );
}
